import React, { useCallback, useEffect, useState } from 'react';
import { API } from '../App';

export default function PlanMembersModal({ plan, token, onCopyReadOnly, copied, onClose }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const response = await fetch(`${API}/api/plans/${plan.id}/members`, { headers: { Authorization: `Bearer ${token}` } });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Could not load collaborators');
    setData(result);
  }, [plan.id, token]);
  useEffect(() => { load().catch(e => setError(e.message)); }, [load]);

  async function invite(event) {
    event.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const response = await fetch(`${API}/api/plans/${plan.id}/invitations`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not send invitation');
      setEmail('');
      setNotice(result.delivery !== 'email_requested'
        ? 'Invitation saved, but email was not delivered. Ask them to sign in to see it.'
        : 'Invitation saved and email requested. They can accept after signing in.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function remove(path) {
    setError(''); setNotice('');
    try {
      const response = await fetch(`${API}${path}`, { method: 'DELETE', headers });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not remove access');
      await load();
    } catch (e) { setError(e.message); }
  }

  return <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,.5)', display: 'grid', placeItems: 'center', padding: 16 }}>
    <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-3)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 16, padding: 24, width: 460, maxWidth: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: 'var(--card-shadow-hover)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <strong>Share this plan</strong><button onClick={onClose} aria-label="Close" style={button}>✕</button>
      </div>
      <div style={{ padding: 12, background: 'var(--bg-4)', borderRadius: 9, border: '1px solid var(--border)' }}>
        <strong style={{ fontSize: 13 }}>Read-only link</strong>
        <p style={{ ...muted, margin: '5px 0 9px' }}>Anyone with this link can view {plan.role === 'editor' ? 'this plan or level' : 'this plan or the current level'}, but cannot edit or chat.</p>
        <button onClick={onCopyReadOnly} style={button}>{copied ? 'Copied!' : 'Copy read-only link'}</button>
      </div>
      {plan.role === 'owner' && <>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>Editors can change cards, use the plan library, and join the shared AI conversation. Only the owner can manage access.</p>
      {data?.role === 'owner' && <form onSubmit={invite} style={{ display: 'flex', gap: 8, margin: '16px 0' }}>
        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" aria-label="Invite email" style={{ flex: 1, minWidth: 0, padding: 9, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-4)', color: 'var(--text-primary)' }} />
        <button disabled={busy} style={{ ...button, background: '#6366f1', color: '#fff' }}>{busy ? 'Inviting…' : 'Invite'}</button>
      </form>}
      {error && <p role="alert" style={{ color: '#ef4444', fontSize: 12 }}>{error}</p>}
      {notice && <p role="status" style={{ color: '#10b981', fontSize: 12 }}>{notice}</p>}
      {!data && !error && <p>Loading…</p>}
      {data && <>
        <h4 style={subhead}>Editors</h4>
        {data.members.length === 0 && <p style={muted}>No editors yet.</p>}
        {data.members.map(member => <div key={member.id} style={row}>
          <span>{member.email}</span>{data.role === 'owner' && <button onClick={() => remove(`/api/plans/${plan.id}/members/${member.id}`)} style={button}>Remove</button>}
        </div>)}
        {data.invitations.length > 0 && <><h4 style={subhead}>Pending invitations</h4>
          {data.invitations.map(invitation => <div key={invitation.id} style={row}>
            <span>{invitation.email}</span>{data.role === 'owner' && <button onClick={() => remove(`/api/plans/${plan.id}/invitations/${invitation.id}`)} style={button}>Revoke</button>}
          </div>)}</>}
      </>}
      </>}
    </div>
  </div>;
}

const button = { border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', background: 'var(--bg-4)', color: 'var(--text-primary)', cursor: 'pointer', font: 'inherit', fontSize: 12 };
const row = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: 13, overflowWrap: 'anywhere' };
const subhead = { fontSize: 12, color: 'var(--text-muted)', margin: '18px 0 5px' };
const muted = { fontSize: 12, color: 'var(--text-muted)' };
